import { GearSix } from '@phosphor-icons/react'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'

import API from '../../../api/api'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import {
  fetchProviders,
  getNotRepliedCount,
  hasConnectedProviders,
  hasGoogleBusinessLocation,
  selectProviders,
  setRefetchRecentReviews,
} from '../../../reducers/reputationManagementReducer'
import Dashboard from '../../Dashboard'
import ModalContainer from '../../ModalContainer/ModelContainer'
import Tabs from '../../Tabs/Tabs'

import DashboardTab from './DashboardTab'
import NotConnected from './NotConnected'
import ReviewsTab from './ReviewsTab'
import SettingsModal from './SettingsModal/SettingsModal'
import Upsell from './Upsell'
import { MODAL_MODE, getPeriodTime } from './helpers'

const ReputationManagement = ({
  reputationAvailable,
  selectedBuilding,
  hasConnectedProviders,
  hasGoogleBusinessLocation,
  fetchProviders = () => {},
  setRefetchRecentReviews = () => {},
  providers = [],
}) => {
  const { t } = useTranslation(['organisation'])
  const [activeTab, setActiveTab] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [modal, showModal] = useState(null)
  const [notRepliedCount, setNotRepliedCount] = useState(0)

  const apartmentsProviderAvailable = providers.some(
    (p) => p.provider === 'apartments' && p.available,
  )

  const getNotRepliedCount = async () => {
    try {
      const { from, to } = getPeriodTime({ value: 'year' })
      const {
        data: { count = 0 },
      } = await API.getReputationReviewNotRepliedCount(selectedBuilding.id, { from, to })
      setNotRepliedCount(count)
    } catch (error) {
      console.error(error)
    }
  }

  const getProviders = async () => {
    try {
      setIsLoading(true)
      await fetchProviders(selectedBuilding.id)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadReputationManagement = async () => {
    getProviders()
    getNotRepliedCount()
  }

  const onModalClose = () => {
    showModal(null)
    setRefetchRecentReviews(true)
  }

  useEffect(() => {
    if (selectedBuilding) {
      loadReputationManagement()
    }
  }, [selectedBuilding])

  return (
    <Dashboard.Container className="ctp-reputation-management">
      {!reputationAvailable ? (
        <Upsell buildingId={selectedBuilding ? selectedBuilding.id : null} />
      ) : (
        <div className={`reputation-management-page ${isLoading ? 'is-loading' : ''}`}>
          <div className="reputation-management-page__header">
            <h2 className="reputation-management-page__title">
              {t('organisation:reputationManagement.page.title')}
            </h2>
            {/*
        TODO: add this back in when we have a link to get started
        <button className="reputation-management-page__btn">
          <Question size={20} />
          Get started
        </button>
        */}
            <button
              onClick={() => showModal(MODAL_MODE.CONNECTIONS)}
              className="reputation-management-page__btn position-right"
            >
              <GearSix size={20} weight="fill" />
              {t('organisation:reputationManagement.page.settings')}
            </button>
          </div>
          <Tabs
            onTabChange={(id) => {
              if (
                hasConnectedProviders &&
                (hasGoogleBusinessLocation || apartmentsProviderAvailable)
              ) {
                setActiveTab(id)
              }
            }}
            activeTab={activeTab}
            className="reputation-management-page__tabs"
          >
            <Tabs.Panel
              heading={
                <div className="heading_text">
                  {t('organisation:reputationManagement.page.dashboardTabHeading')}
                </div>
              }
            >
              {!hasConnectedProviders ? (
                <NotConnected showModal={() => showModal(MODAL_MODE.CONNECTIONS)} />
              ) : (
                <DashboardTab
                  goToReviews={() => setActiveTab(1)}
                  showModal={() => showModal(MODAL_MODE.LOCATIONS)}
                  buildingId={selectedBuilding.id}
                />
              )}
            </Tabs.Panel>
            <Tabs.Panel
              heading={
                <div className="heading_text">
                  {t('organisation:reputationManagement.page.reviewsTabHeading')}
                  {notRepliedCount > 0 && (
                    <span className="reputation-management-page__tabs--label">
                      {notRepliedCount}
                    </span>
                  )}
                </div>
              }
            >
              {!hasConnectedProviders ? (
                <NotConnected showModal={() => showModal(MODAL_MODE.CONNECTIONS)} />
              ) : (
                <ReviewsTab buildingId={selectedBuilding.id} />
              )}
            </Tabs.Panel>
          </Tabs>
          {!!modal && (
            <ModalContainer
              onClose={() => showModal(null)}
              headerTitle={t('organisation:reputationManagement.page.title')}
              headerIcon={<GearSix size={20} />}
            >
              <SettingsModal
                onClose={onModalClose}
                buildingId={selectedBuilding.id}
                mode={modal}
                onConnected={() => loadReputationManagement()}
              />
            </ModalContainer>
          )}
        </div>
      )}
    </Dashboard.Container>
  )
}

ReputationManagement.propTypes = {
  reputationAvailable: PropTypes.bool,
  selectedBuilding: PropTypes.object,
  hasConnectedProviders: PropTypes.bool,
  hasGoogleBusinessLocation: PropTypes.bool,
  notRepliedCount: PropTypes.number,
  fetchProviders: PropTypes.func,
  setRefetchRecentReviews: PropTypes.func,
}

export default connect(
  (state) => {
    const currentBuilding = selectActiveBuilding(state)
    return {
      reputationAvailable: currentBuilding ? currentBuilding.reputationAvailable : false,
      selectedBuilding: currentBuilding,
      hasConnectedProviders: hasConnectedProviders(state),
      hasGoogleBusinessLocation: hasGoogleBusinessLocation(state),
      notRepliedCount: getNotRepliedCount(state),
      providers: selectProviders(state),
    }
  },
  { fetchProviders, setRefetchRecentReviews },
)(ReputationManagement)
